import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { aboutInfo } from '@/mocks/siteData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rd6gcnlsngrm4lmag0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 新金瓶梅',
    description: aboutInfo.brandIntro,
    url: 'https://xinjinpingmei.com/about',
  };

  const faqPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '新金瓶梅是一个什么样的平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: aboutInfo.brandIntro,
        },
      },
      {
        '@type': 'Question',
        name: '如何联系新金瓶梅团队？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以通过以下方式联系我们：邮箱 ${aboutInfo.contact.email}，电话 ${aboutInfo.contact.phone}，地址 ${aboutInfo.contact.address}`,
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqPageSchema)}</script>
      <Navbar />
      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="container-site py-12 md:py-16">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-3">
              关于我们
            </h1>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="container-site max-w-[900px]">
            <div className="text-center mb-12">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                品牌介绍
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
                {aboutInfo.brandIntro}
              </p>
            </div>
            <div className="bg-background-100 border border-background-200/70 rounded-lg p-8 md:p-10">
              <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-4">我们的使命</h3>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {aboutInfo.mission}
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 md:py-16 bg-primary-500">
          <div className="container-site">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {aboutInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-background-50/80">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="container-site">
            <div className="text-center mb-12">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                学术声誉
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                我们始终坚持学术品质，致力于成为金学研究领域最值得信赖的平台
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {aboutInfo.features.map((feature) => (
                <div key={feature.title} className="text-center p-6">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                    <i className={`${feature.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="container-site max-w-[1000px]">
            <div className="text-center mb-12">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-heading font-semibold text-foreground-950 mb-1">地址</h4>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-heading font-semibold text-foreground-950 mb-1">邮箱</h4>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-heading font-semibold text-foreground-950 mb-1">电话</h4>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-heading font-semibold text-foreground-950 mb-1">微信</h4>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8 space-y-5"
              >
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">发送消息</h3>

                <div>
                  <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="about-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>

                <div>
                  <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="about-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>

                <div>
                  <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="about-message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                    placeholder="请输入您的留言内容（500字以内）"
                  />
                </div>

                <div className="honeypot-wrapper">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  发送消息
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-green-600 text-center mt-3">
                    消息发送成功！我们会尽快回复您。
                  </p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-600 text-center mt-3">
                    {formError}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}